import { useRef } from 'react';
import { DecodeReveal } from './DecodeReveal';
import { useInView } from '../hooks/useInView';

// Illustrative only — no real records. Deliberately mundane recoveries
// (a missed plan, a declined request, an unexplained change) rather than
// anything graphic, in keeping with the tone of the rest of the site. Mixed
// card types and sources so the showcase isn't just redaction-recovery on
// repeat, and covers more of what the product actually does beyond reading:
// matching the same record across organisations and suggesting who else to
// ask, not just restoring blacked-out text.
const EXAMPLES = [
  {
    type: 'redacted',
    date: '14 MAR 2011',
    source: 'LAC REVIEW',
    lead: 'Concerns were raised about the stability of the placement.',
    hidden: 'The carer indicated she could not continue beyond the summer term.',
  },
  {
    type: 'placement',
    date: '03 SEP 2015',
    source: 'PLACEMENT CHANGE',
    body: "Moved from a long-term foster placement to a children's home in Leeds. No reason for the move was recorded at the time.",
    footer: 'Placement history rebuilt from five different case files.',
  },
  {
    type: 'redacted',
    date: '30 JAN 2013',
    source: 'FAMILY COURT ORDER',
    lead: 'The order was made in the terms set out below.',
    hidden: 'Supervised contact only, reviewed after six months.',
  },
  {
    type: 'crossref',
    date: 'SPRING TERM 2014',
    source: 'SCHOOL REPORT',
    headline: 'The same end-of-term report turned up in three separate replies.',
    orgs: ['Elm Park Primary', 'Leeds City Council', 'GP surgery'],
    footer: 'Matched automatically. The wording was almost identical in each.',
  },
  {
    type: 'safeguarding',
    date: '11 FEB 2010',
    source: 'REFERRAL · SOCIAL SERVICES',
    mentions: 'a safeguarding referral',
    body: 'A referral was made after a concern was raised at school. The outcome of the assessment is recorded in the file.',
    support: [
      { name: 'Childline', phone: '0800 1111' },
      { name: 'NSPCC', phone: '0808 800 5000' },
    ],
  },
  {
    type: 'redacted',
    date: '11 NOV 2014',
    source: 'HEALTH CHECK',
    lead: 'Routine check-up, growth and development on track.',
    hidden: 'Patient mentioned feeling unsettled after a recent house move.',
  },
  {
    type: 'suggestion',
    date: '11 NOV 2014',
    source: 'MENTIONED IN THIS FILE',
    headline: "This GP record mentions a stay at Sheffield Children's Hospital in 2013.",
    body: "There's no request to them in your list yet.",
    suggested: "Sheffield Children's Hospital",
  },
  {
    type: 'redacted',
    date: '07 JAN 2012',
    source: 'CONTACT REVIEW',
    lead: 'Contact arrangements were reviewed as planned.',
    hidden: "Grandmother's request for extra visits was declined without a stated reason.",
  },
  {
    type: 'placement',
    date: '16 JUL 2017',
    source: 'PLACEMENT CHANGE',
    body: 'Returned to kinship care with an aunt after eight months in emergency foster care.',
    footer: 'Pieced together from three organisations that never directly told each other.',
  },
  {
    type: 'redacted',
    date: '23 APR 2016',
    source: 'CARE PLAN UPDATE',
    lead: 'Plan remains on track for the coming year.',
    hidden: 'A previously agreed change of school did not go ahead as promised.',
  },
];

const CardShell = ({ innerRef, children }) => (
  <div ref={innerRef} className="bg-surface rounded-lg p-6 sm:p-8 shrink-0">
    {children}
  </div>
);

const RedactedCard = ({ example, containerRef }) => {
  // The ref goes on the line containing the actual hidden text, not the
  // whole (much taller) card — checking the card's own visibility was the
  // bug: a card can be 300px+ tall, so its top can already be scrolled out
  // of view while its bottom (and the hidden phrase, partway down) is
  // still showing, or vice versa entering from the bottom. Watching the
  // exact element that decodes means "visible" and "decoded" always mean
  // the same thing, with no margin/timing guesswork needed.
  //
  // Decode once 60% of the line is showing, but only re-redact once it has
  // left the container entirely: with a symmetric threshold it snapped back
  // to black the moment its top slid under the fade at the top edge, while
  // most of the line was still readable.
  const [ref, inView] = useInView({ root: containerRef, threshold: 0.6, exitThreshold: 0 });
  return (
    <CardShell>
      <p className="pd-mono text-ink-55">
        {example.date} · {example.source}
      </p>
      <p ref={ref} className="text-[18px] leading-[1.85] mt-3">
        {example.lead} <DecodeReveal text={example.hidden} active={inView} />
      </p>
      <hr className="border-rule my-5" />
      <p className="text-sm text-ink-55">This sentence was blacked out in one copy. We recovered it from another.</p>
    </CardShell>
  );
};

const PlacementCard = ({ example }) => (
  <CardShell>
    <p className="pd-mono text-ink-55">
      {example.date} · {example.source}
    </p>
    <p className="text-[18px] leading-[1.85] mt-3">{example.body}</p>
    <hr className="border-rule my-5" />
    <p className="text-sm text-ink-55">{example.footer}</p>
  </CardShell>
);

const CrossrefCard = ({ example }) => (
  <CardShell>
    <p className="pd-mono text-ink-55">
      {example.date} · {example.source}
    </p>
    <p className="text-[18px] leading-[1.85] mt-3">{example.headline}</p>
    <div className="flex flex-wrap gap-2 mt-4">
      {example.orgs.map((org) => (
        <span key={org} className="text-sm font-medium text-ink-70 bg-ground rounded-full px-3 py-1">
          {org}
        </span>
      ))}
    </div>
    <hr className="border-rule my-5" />
    <p className="text-sm text-ink-55">{example.footer}</p>
  </CardShell>
);

const SuggestionCard = ({ example }) => (
  <CardShell>
    <p className="pd-mono text-ink-55">
      {example.date} · {example.source}
    </p>
    <p className="text-[18px] leading-[1.85] mt-3">{example.headline}</p>
    <p className="text-sm text-ink-55 mt-2">{example.body}</p>
    <div className="mt-4">
      <span className="inline-flex items-center gap-2 text-sm font-medium text-brand-ink bg-brand-wash rounded-full px-4 py-2 border border-dashed border-brand/40">
        + {example.suggested}
      </span>
    </div>
    <hr className="border-rule my-5" />
    <p className="text-sm text-ink-55">Suggested from what&apos;s already in your file, not a guess.</p>
  </CardShell>
);

const SafeguardingCard = ({ example }) => (
  <div className="bg-care-wash rounded-lg p-6 sm:p-8 shrink-0">
    <p className="pd-mono text-ink-55">
      {example.date} · {example.source}
    </p>
    <p className="pd-mono text-care-ink mt-4">Before you open this</p>
    <p className="text-[17px] leading-relaxed text-ink mt-2">
      This record mentions {example.mentions}. You can read it at your own pace, and stop whenever you want.
    </p>
    <ul className="flex flex-col gap-2 mt-4">
      {example.support.map((org) => (
        <li key={org.name} className="rounded-md bg-surface px-4 py-3 flex items-center justify-between gap-3">
          <span className="font-semibold text-ink text-[15px]">{org.name}</span>
          <span className="font-mono text-[15px] tracking-[0.04em] text-care-ink">{org.phone}</span>
        </li>
      ))}
    </ul>
  </div>
);

const CARD_BY_TYPE = {
  placement: PlacementCard,
  safeguarding: SafeguardingCard,
  crossref: CrossrefCard,
  suggestion: SuggestionCard,
};

const RecordCard = ({ example, containerRef }) => {
  const Card = CARD_BY_TYPE[example.type];
  if (Card) return <Card example={example} />;
  return <RedactedCard example={example} containerRef={containerRef} />;
};

/**
 * A small looping showcase in the hero: cards scroll slowly upward as if
 * someone were paging through recovered records — a mix of redaction
 * recovery, plain placement history, a safeguarding referral (shown with
 * the app's real support-hint treatment), a record matched across multiple
 * organisations, and a suggested organisation to request from next. Each
 * redacted card re-decodes every time it scrolls back into view (and
 * re-redacts once it is fully out of view, ready for the next pass), rather
 * than only once. Pauses on hover;
 * falls back to a plain, manually-scrollable list under
 * prefers-reduced-motion.
 */
export const RecordScrollShowcase = () => {
  const containerRef = useRef(null);
  const reducedMotion =
    typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  return (
    <div
      ref={containerRef}
      className={`pd-record-track-wrap relative rounded-lg ${reducedMotion ? 'overflow-y-auto' : 'overflow-hidden'} h-[440px] sm:h-[500px]`}
      aria-label="Examples of the sorts of things recovered records can show"
    >
      {!reducedMotion && (
        <>
          <div className="pointer-events-none absolute inset-x-0 top-0 h-10 bg-gradient-to-b from-ground to-transparent z-10" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-gradient-to-t from-ground to-transparent z-10" />
        </>
      )}
      <div className={`flex flex-col gap-4 ${reducedMotion ? '' : 'pd-record-track'}`}>
        {(reducedMotion ? EXAMPLES : [...EXAMPLES, ...EXAMPLES]).map((example, i) => (
          <RecordCard key={i} example={example} containerRef={containerRef} />
        ))}
      </div>
    </div>
  );
};

export default RecordScrollShowcase;
